import { useEffect } from "react";

/**
 * Injects a JSON-LD <script> into the document head with a stable id.
 * Removes/replaces any prior script with the same id so route changes
 * do not pile up stale schema.
 */
export default function JsonLd({ id, data }) {
  useEffect(() => {
    if (!id || !data) return;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.text = JSON.stringify(data);
    return () => {
      const same = document.getElementById(id);
      if (same) same.remove();
    };
  }, [id, data]);
  return null;
}

export const SITE_URL = "https://cochlearimplantsurgery.in";
export const SITE_NAME = "CochlearImplantSurgery.in";
export const SITE_DESC =
  "India's Cochlear Implant Knowledge, Access & Rehabilitation Platform — Powered by HearClear.";
export const ORG_NAME = "HearClear";
export const SAME_AS = [];

export const organizationLd = {
  "@context": "https://schema.org",
  "@type": "MedicalOrganization",
  name: ORG_NAME,
  alternateName: SITE_NAME,
  url: SITE_URL,
  description: SITE_DESC,
  medicalSpecialty: ["Otolaryngology", "Audiology"],
  sameAs: SAME_AS,
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  description: SITE_DESC,
  publisher: { "@type": "Organization", name: ORG_NAME },
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/knowledge-centre?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export function breadcrumbLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

export function articleLd(article, slug) {
  return {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    headline: article.title,
    description: article.summary,
    inLanguage: "en-IN",
    url: `${SITE_URL}/article/${slug}`,
    datePublished: article.created_at,
    dateModified: article.last_reviewed || article.created_at,
    author: { "@type": "Organization", name: ORG_NAME },
    publisher: { "@type": "Organization", name: ORG_NAME },
    reviewedBy: { "@type": "Organization", name: article.reviewer || ORG_NAME },
    about: { "@type": "MedicalCondition", name: "Hearing Loss" },
    medicalAudience: ["Patient", "MedicalResearch"],
  };
}

export function faqLd(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export function newsArticleLd(news) {
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: news.title,
    description: news.summary,
    inLanguage: "en-IN",
    url: `${SITE_URL}/news/${news.slug}`,
    image: news.image ? [news.image] : undefined,
    datePublished: news.published_at,
    dateModified: news.published_at,
    publisher: { "@type": "Organization", name: ORG_NAME },
    author: { "@type": "Organization", name: news.source || ORG_NAME },
  };
}

export function physicianLd(doc) {
  return {
    "@context": "https://schema.org",
    "@type": "Physician",
    name: doc.name,
    medicalSpecialty: doc.specialty,
    address: {
      "@type": "PostalAddress",
      addressLocality: doc.city,
      addressRegion: doc.state,
      addressCountry: "IN",
    },
    image: doc.profile_photo,
    description: doc.bio,
  };
}

export function hospitalLd(h) {
  return {
    "@context": "https://schema.org",
    "@type": "Hospital",
    name: h.name,
    medicalSpecialty: ["Otolaryngology", "Cochlear Implantation"],
    address: {
      "@type": "PostalAddress",
      addressLocality: h.city,
      addressRegion: h.state,
      addressCountry: "IN",
      streetAddress: h.address,
    },
    image: h.image,
    description: h.description,
  };
}

export function itemListLd(items, urlPrefix, nameKey = "name") {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE_URL}${urlPrefix}${it.slug || it.id}`,
      name: it[nameKey],
    })),
  };
}
